import { notify } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import { DocsService } from '@affine/core/modules/doc';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import {
  AccessibleFolderCacheStore,
  FolderStore,
} from '@affine/core/modules/organize';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import { useConnectivityStatus } from '../use-connectivity-status';
import { useNavigateHelper } from '../use-navigate-helper';

export function useBlockSuiteMetaHelper() {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const { openPage } = useNavigateHelper();
  const docsService = useService(DocsService);
  const docRecordList = docsService.list;
  const connectivityStatus = useConnectivityStatus();
  const isCloudWorkspace = workspace.flavour !== 'local';
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderStore = useService(FolderStore);

  const notifyConnectionRequired = useCallback(() => {
    notify.error({
      title: t['com.streamlain.settings.connection-required.title'](),
      message: t['com.streamlain.settings.connection-required.description'](),
    });
  }, [t]);

  const discardLocalCloudDoc = useCallback(
    async (docId: string): Promise<boolean> => {
      await docsService.discardLocalDoc(docId);
      accessibleFolderCache.optimisticRemoveDocEverywhere(docId);
      folderStore.removeDocLinksLocalOnly(docId);
      notify.success({ title: t['Deleted']() });
      return true;
    },
    [accessibleFolderCache, docsService, folderStore, t]
  );

  // TODO-Doma
  // "Remove" may cause ambiguity here. Consider renaming as "moveToTrash".
  const removeToTrash = useCallback(
    async (docId: string): Promise<boolean> => {
      if (isCloudWorkspace && !connectivityStatus.backendAvailable) {
        if (docsService.isLocallyCreated(docId)) {
          return discardLocalCloudDoc(docId);
        }
        notifyConnectionRequired();
        return false;
      }
      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        return docRecord.moveToTrash();
      }
      return false;
    },
    [
      connectivityStatus.backendAvailable,
      discardLocalCloudDoc,
      docRecordList,
      docsService,
      isCloudWorkspace,
      notifyConnectionRequired,
    ]
  );

  const restoreFromTrash = useCallback(
    (docId: string) => {
      if (isCloudWorkspace && !connectivityStatus.backendAvailable) {
        notifyConnectionRequired();
        return false;
      }
      const docRecord = docRecordList.doc$(docId).value;
      if (docRecord) {
        return docRecord.restoreFromTrash();
      }
      return false;
    },
    [
      connectivityStatus.backendAvailable,
      docRecordList,
      isCloudWorkspace,
      notifyConnectionRequired,
    ]
  );

  const permanentlyDeletePage = useCallback(
    async (pageId: string): Promise<boolean> => {
      // sl-o0ra: Cloud workspaces use AccessibleDocCacheStore (local-first + server sync)
      if (isCloudWorkspace) {
        if (!connectivityStatus.backendAvailable) {
          if (docsService.isLocallyCreated(pageId)) {
            return await discardLocalCloudDoc(pageId);
          }
          notifyConnectionRequired();
          return false;
        }
        accessibleFolderCache.optimisticRemoveDocEverywhere(pageId);
        folderStore.removeDocLinksLocalOnly(pageId);
        await accessibleDocCache.removeDoc(pageId);
      }
      // Disconnect the loaded Y.Doc before deleting nbstore state. Otherwise a
      // still-connected doc can immediately write a fresh local snapshot/clock.
      workspace.docCollection.removeDoc(pageId);
      if (isCloudWorkspace) {
        await workspace.engine.doc.deleteDoc(pageId);
      }
      return true;
    },
    [
      accessibleDocCache,
      accessibleFolderCache,
      connectivityStatus.backendAvailable,
      discardLocalCloudDoc,
      docsService,
      folderStore,
      isCloudWorkspace,
      notifyConnectionRequired,
      workspace,
    ]
  );

  const duplicate = useAsyncCallback(
    async (pageId: string, openPageAfterDuplication: boolean = true) => {
      const newPageId = await docsService.duplicate(pageId);
      openPageAfterDuplication &&
        openPage(workspace.docCollection.id, newPageId);
    },
    [docsService, openPage, workspace.docCollection.id]
  );

  return {
    removeToTrash,
    restoreFromTrash,
    permanentlyDeletePage,

    duplicate,
  };
}
